import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, FileText, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagePages() {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '' });
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pages/all`, { headers: { Authorization: `Bearer ${token}` } });
      setPages(res.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleCreate = async () => {
    if (!form.title || !form.slug) { toast.error('Titre et slug requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/pages`, { ...form, blocks: [], is_published: false }, { headers });
      toast.success('Page créée');
      setDialogOpen(false);
      setForm({ title: '', slug: '' });
      fetchPages();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    try {
      await axios.delete(`${API}/pages/${id}`, { headers });
      toast.success('Page supprimée');
      fetchPages();
    } catch (e) { toast.error('Erreur'); }
  };

  const togglePublish = async (page) => {
    try {
      await axios.put(`${API}/pages/${page.id}`, { is_published: !page.is_published }, { headers });
      toast.success(page.is_published ? 'Page dépubliée' : 'Page publiée');
      fetchPages();
    } catch (e) { toast.error('Erreur'); }
  };

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <div data-testid="manage-pages">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Site Builder</h1>
          <p className="text-sm text-[#6B7C93]">{pages.length} page(s)</p>
        </div>
        <Button onClick={() => { setForm({ title: '', slug: '' }); setDialogOpen(true); }} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-page-btn">
          <Plus className="w-4 h-4 mr-2" />Nouvelle page
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titre</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Blocs</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Modifié</TableHead>
                <TableHead className="w-36">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell className="font-medium">{page.title}</TableCell>
                  <TableCell className="text-sm text-[#6B7C93] font-mono">/{page.slug}</TableCell>
                  <TableCell><span className="text-sm">{page.blocks?.length || 0} bloc(s)</span></TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${page.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {page.is_published ? 'Publié' : 'Brouillon'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-[#6B7C93]">{page.updated_at ? new Date(page.updated_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => togglePublish(page)} title={page.is_published ? 'Dépublier' : 'Publier'}>
                        {page.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" asChild data-testid={`edit-page-${page.id}`}>
                        <a href={`/admin/site-builder/${page.id}`}><Pencil className="w-4 h-4" /></a>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(page.id)} className="text-red-500" data-testid={`delete-page-${page.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && pages.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="mb-2">Aucune page</p>
              <p className="text-sm">Créez votre première page avec le constructeur visuel</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nouvelle page</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ title: e.target.value, slug: slugify(e.target.value) })}
                placeholder="Ma nouvelle page"
                data-testid="page-title-input"
              />
            </div>
            <div>
              <Label>Slug (URL) *</Label>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-sm text-[#94A3B8]">/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="ma-nouvelle-page"
                  className="font-mono text-sm"
                  data-testid="page-slug-input"
                />
              </div>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="page-create-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer et ouvrir l'éditeur
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
